import * as React from 'react';
// Import material ui modules
import {
	Chat,
} from '@mui/icons-material';
// Import hooks.
import {
	useFormRef,
	UseFormRefResults,
} from '@app/hooks/view';
import useListPolicyNote, {
	UseListPolicyNoteResults
} from './UseListPolicyNote';
// Import props.
import {
	ListPolicyNotesViewProps,
	ListProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
} from '@app/enumerations';

interface UseListPolicyNotesViewParameters {
	id: string;
	props: ListPolicyNotesViewProps,
}

interface UseListPolicyNotesViewResults {
	policyNoteList: UseListPolicyNoteResults;
	applyFormProps: () => ListProps & ScrollbarProps;
}

const useListPolicyNotesView = (params: UseListPolicyNotesViewParameters): UseListPolicyNotesViewResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): ListProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Notes',
			emptyBackgroundIcon: Chat,
			emptyBackgroundMessage: 'There are no notes',
			onAddClick: params.props.onAddPolicyNote,
			onRefreshClick: params.props.onRefreshPolicyNotes,
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			onCloseClick: params.props.onClose,
			...formRef.applyProps(),
		};
	};

	// #endregion exported methods


	// #region private methods

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for policy note list item.
	 **/
	const generateIdForPolicyNoteList = (): string => {
		return `${generateIdForForm()}-policyNoteList`;
	}

	//#endregion id generators

	// #endregion private methods


	// #region variables

	// Instantiate the form ref hook.
	const formRef: UseFormRefResults = useFormRef();

	// Instantiate the 'policy note' list hook.
	const policyNoteList: UseListPolicyNoteResults = useListPolicyNote({
		id: generateIdForPolicyNoteList(),
		policyNotes: params.props.policyNotes,
		onAddNoteToPolicyNote: params.props.onAddNoteToPolicyNote,
		onChangeFlagDate: params.props.onChangeFlagDate,
		onMarkAsComplete: params.props.onMarkAsComplete,
		onArchivePolicyNote: params.props.onArchivePolicyNote,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		if (params.props.policyNotes.length > 0) {
			let policyNoteId: number | undefined = params.props.policyNotes[0]?.id ?? undefined;
			if (policyNoteId !== undefined) {
				policyNoteList.focusToListItemRecord(policyNoteId.toString());
			}
		}
		else {
			formRef.focusToForm();
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		policyNoteList: policyNoteList,
		applyFormProps: applyFormProps,
	};
}

// Export parameters.
export {
	UseListPolicyNotesViewParameters,
}

// Export results.
export {
	UseListPolicyNotesViewResults,
}

export default useListPolicyNotesView;